import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  Help,
  BugReport,
  Feedback,
  Support,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import { useTranslation } from 'react-i18next';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Send to real API endpoint
      const response = await apiService.sendContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      
      console.log('Contact form sent:', response.data);
      setShowSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setShowSuccess(false), 10000);
    } catch (error: any) {
      console.error('Failed to send contact form:', error);
      setError(error.message || t('contactPage.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: <Email sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('contactPage.methods.email.title'),
      description: t('contactPage.methods.email.description'),
      contact: 'support@rideclubnet.com',
      action: 'mailto:support@rideclubnet.com',
    },
    {
      icon: <Phone sx={{ fontSize: 40, color: 'success.main' }} />,
      title: t('contactPage.methods.phone.title'),
      description: t('contactPage.methods.phone.description'),
      contact: '1-800-RIDE-CLUB',
      action: 'tel:1-800-743-3258',
    },
    {
      icon: <LocationOn sx={{ fontSize: 40, color: 'info.main' }} />,
      title: t('contactPage.methods.office.title'),
      description: t('contactPage.methods.office.description'),
      contact: 'Toronto, ON',
      action: '#',
    },
  ];

  const supportTopics = [
    {
      icon: <Help />,
      title: t('contactPage.supportTopics.general.title'),
      description: t('contactPage.supportTopics.general.description'),
    },
    {
      icon: <BugReport />,
      title: t('contactPage.supportTopics.technical.title'),
      description: t('contactPage.supportTopics.technical.description'),
    },
    {
      icon: <Support />,
      title: t('contactPage.supportTopics.safety.title'),
      description: t('contactPage.supportTopics.safety.description'),
    },
    {
      icon: <Feedback />,
      title: t('contactPage.supportTopics.feedback.title'),
      description: t('contactPage.supportTopics.feedback.description'),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #81C784 0%, #4CAF50 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 2 }}>
              {t('contactPage.hero.title')}
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              {t('contactPage.hero.subtitle')}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
          {/* Contact Form */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 66%' } }}>
            <Card sx={{ p: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                {t('contactPage.form.title')}
              </Typography>
              
              {showSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {t('contactPage.form.success')}
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                    <TextField
                      fullWidth
                      label={t('contactPage.form.fullName')}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />


                    <TextField
                      fullWidth
                      label={t('contactPage.form.email')}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Box>

                    <TextField
                      fullWidth
                      label={t('contactPage.form.subject')}
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />

                    <TextField
                      fullWidth
                      label={t('contactPage.form.message')}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={6}
                      variant="outlined"
                      placeholder={t('contactPage.form.placeholder')}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={loading ? undefined : <Send />}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading ? t('contactPage.form.sending') : t('contactPage.form.send')}
                    </Button>
                </Box>
              </Box>
            </Card>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33%' } }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                {t('contactPage.methods.title')}
              </Typography>
              <Stack spacing={3}>
                {contactMethods.map((method, index) => (
                  <Box key={index}>
                    <Card
                      component="a"
                      href={method.action}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        p: 3,
                        '&:hover': {
                          boxShadow: (theme) => theme.shadows[4],
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s ease-in-out',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {method.icon}
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {method.title}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {method.description}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {method.contact}
                      </Typography>
                    </Card>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Support Topics */}
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
            {t('contactPage.supportTopics.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', color: 'text.secondary', mb: 6, maxWidth: 800, mx: 'auto' }}
          >
            {t('contactPage.supportTopics.subtitle')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            {supportTopics.map((topic, index) => (
              <Box key={index}>
                <Card sx={{ height: '100%', p: 3, textAlign: 'center' }}>
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {topic.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {topic.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {topic.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {t('contactPage.faq.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('contactPage.faq.subtitle')}
          </Typography>
          <Button
            variant="outlined"
            size="large"
            href="/help"
            sx={{ mr: 2 }}
          >
            {t('contactPage.faq.helpCenter')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            href="/support"
          >
            {t('contactPage.faq.safetyAndSupport')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactPage;